/**
 * Search Management - Moon Moon Agency
 * Handles search overlay functionality
 */

/*********************************************************************************************
 * 🔍 SEARCH MODULE
 */
const SearchManager = {
	// Local configuration
	config: {
		selectors: {
				searchContainer: '[data-search-container]',
			searchOverlay: '[data-search-overlay]',
			stickyHeader: '.mm-section-header',
			pillsSplide: '.mm-search-pills-splide',
			productsSplide: '.mm-search-products-slider',
			searchInput: '[data-search-input]',
			searchToggle: '[data-search-toggle]',
			searchClose: '[data-search-close]',
			searchClear: '[data-search-clear]',
			predictiveSearch: '[data-predictive-search]'
		},
		classes: {
			stopScrolling: 'mm-stop-scrolling',
			open: 'mm-open'
		},
		timing: {
			searchFocusDelay: 100,
			searchBlurDelay: 100
		}
	},

	// Gestionnaires de scroll lock (sans overflow:hidden qui casse position:sticky)
	_scrollLockHandler: null,

	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Initialize the search module
	 */
	init() {
		const search = window.getElement(this.config.selectors.searchContainer);
		if (!search) return;

		this._bindEvents();
	},

	/**
	 * Bind all static event listeners (called once at init)
	 */
	_bindEvents() {
		// Close buttons (X button in bar + background overlay)
		window.getElements(this.config.selectors.searchClose).forEach((btn) => {
			btn.addEventListener('click', () => this.hide());
		});

		// Clic sur l'overlay ferme la search
		const overlay = window.getElement(this.config.selectors.searchOverlay);
		if (overlay) overlay.addEventListener('click', () => this.hide());

		// Clear button (inside search input)
		const searchClearBtn = window.getElement(this.config.selectors.searchClear);
		if (searchClearBtn) {
			searchClearBtn.addEventListener('click', () => this.hide());
		}

		// Predictive search touchstart — enregistré une seule fois (pas dans show())
		const predictiveSearch = window.getElement(this.config.selectors.predictiveSearch);
		if (predictiveSearch) {
			predictiveSearch.addEventListener('touchstart', () => {
				const searchInput = window.getElement(this.config.selectors.searchInput);
				if (searchInput) {
					setTimeout(() => searchInput.blur(), this.config.timing.searchBlurDelay);
				}
			}, { passive: true });
		}
	},

	/**
	 * Update aria-expanded on all search toggle buttons
	 */
	_updateToggleAria(isOpen) {
		window.getElements(this.config.selectors.searchToggle).forEach((btn) => {
			btn.setAttribute('aria-expanded', String(isOpen));
		});
	},

	/**
	 * Setup focus trap for search overlay
	 */
	setupFocusTrap(searchContainer, triggerElement) {
		const focusableElements = searchContainer.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes search
			if (e.key === 'Escape') {
				e.preventDefault();
				this.hide();
				this.focusTrap.triggerElement?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		window.log('info', 'SearchManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'SearchManager', 'Focus trap removed');
		}
	},

	/**
	 * Wire external prev/next arrows (in header row) to the products Splide instance
	 */
	_bindProductsArrows(splide) {
		const header = document.querySelector('.mm-search-featured-header');
		if (!header) return;

		const prev = header.querySelector('.splide__arrow--prev');
		const next = header.querySelector('.splide__arrow--next');
		if (!prev || !next) return;

		prev.addEventListener('click', () => splide.go('<'));
		next.addEventListener('click', () => splide.go('>'));

		const updateArrowsState = () => {
			const end = splide.Components.Controller.getEnd();
			prev.disabled = splide.index <= 0;
			next.disabled = splide.index >= end;
		};

		splide.on('mounted moved refresh', updateArrowsState);
		updateArrowsState();
	},

	/**
	 * Show search overlay
	 */
	show(triggerElement) {
		const search = window.getElement(this.config.selectors.searchContainer);
		const searchInput = window.getElement(this.config.selectors.searchInput);

		if (!search || !searchInput) {
			window.log('warn', 'SearchManager', 'Required elements not found');
			return;
		}

		const overlay = window.getElement(this.config.selectors.searchOverlay);

		// Positionne le panel et l'overlay sous le bas réel du header sticky
		const stickyHeader = window.getElement(this.config.selectors.stickyHeader);
		if (stickyHeader) {
			const headerBottom = stickyHeader.getBoundingClientRect().bottom;
			search.style.top = `${headerBottom}px`;
			if (overlay) overlay.style.top = `${headerBottom}px`;
		}

		// Init Splide pills sur mobile (une seule fois)
		if (window.isMobile() && typeof Splide !== 'undefined') {
			const pillsSplide = window.getElement(this.config.selectors.pillsSplide);
			if (pillsSplide && !pillsSplide.splide) {
				new Splide(pillsSplide).mount();
			}
		}

		// Init Splide produits mis en avant (desktop, une seule fois)
		if (typeof Splide !== 'undefined') {
			const productsSplide = window.getElement(this.config.selectors.productsSplide);
			if (productsSplide && !productsSplide.splide) {
				productsSplide.splide = new Splide(productsSplide).mount();
				this._bindProductsArrows(productsSplide.splide);
			}
		}

		if (window.LenisManager?.instance) {
			window.LenisManager.instance.stop();
		} else {
			// Fallback sans Lenis : bloque scroll page mais autorise scroll dans le container search
			const searchContainer = window.getElement(this.config.selectors.searchContainer);
			this._scrollLockHandler = (e) => {
				if (searchContainer && searchContainer.contains(e.target)) return;
				e.preventDefault();
			};
			window.addEventListener('wheel', this._scrollLockHandler, { passive: false });
			window.addEventListener('touchmove', this._scrollLockHandler, { passive: false });
		}
		search.classList.add(this.config.classes.open);
		search.setAttribute('aria-hidden', 'false');
		if (overlay) overlay.classList.add(this.config.classes.open);
		this._updateToggleAria(true);

		if (window.NavigationManager?.showBackgroundFilter) {
			window.NavigationManager.showBackgroundFilter();
		}

		setTimeout(() => {
			searchInput.focus();
			this.setupFocusTrap(search, triggerElement);
		}, this.config.timing.searchFocusDelay);

		window.emit('global:search-opened');
	},

	/**
	 * Hide search overlay
	 */
	hide(keepFilterActive = false, keepStopScrolling = false) {
		const search = window.getElement(this.config.selectors.searchContainer);
		const searchInput = window.getElement(this.config.selectors.searchInput);

		if (!search || !searchInput) {
			window.log('warn', 'SearchManager', 'Required elements not found for hiding');
			return;
		}

		const overlay = window.getElement(this.config.selectors.searchOverlay);

		// Réinitialise le top inline (retour au CSS)
		search.style.top = '';
		if (overlay) overlay.style.top = '';

		if (!keepStopScrolling) {
			if (window.LenisManager?.instance) {
				window.LenisManager.instance.start();
			} else if (this._scrollLockHandler) {
				window.removeEventListener('wheel', this._scrollLockHandler);
				window.removeEventListener('touchmove', this._scrollLockHandler);
				this._scrollLockHandler = null;
			}
		}
		search.classList.remove(this.config.classes.open);
		search.setAttribute('aria-hidden', 'true');
		if (overlay) overlay.classList.remove(this.config.classes.open);
		this._updateToggleAria(false);

		this.removeFocusTrap();

		if (!keepFilterActive && window.NavigationManager?.hideBackgroundFilter) {
			window.NavigationManager.hideBackgroundFilter();
		}

		searchInput.value = '';
		searchInput.dispatchEvent(new Event('input'));

		window.emit('global:search-closed', { keepFilterActive, keepStopScrolling });
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.SearchManager = SearchManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmShowSearch = (triggerElement) => SearchManager.show(triggerElement);
window.mmHideSearch = (keepFilter, keepScrolling) => SearchManager.hide(keepFilter, keepScrolling);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => SearchManager.init());
} else {
	SearchManager.init();
}
